import { useState } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import { FaCheck } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useSnackbarContext } from "components/snackbar";
import { IPropertySelected } from "../interface";
import { Button } from "components/buttonV2";
import { useHistory } from "react-router-dom";
import { ISelectedPlan } from "../interface";
import Properties from "views/properties";
import SelectPropertiesContainer from "../components/SelectPropertiesContainer";
import ServicePlanContainer from "../components/ServicePlanContainer";
import Success from "../components/Success";
import Spinner from "components/spinner";

const BulkSend = () => {
  const { t } = useTranslation();
  const tBase = "views.service-plan-dashboard.views.BulkSend";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { showSnackbar } = useSnackbarContext();
  const history = useHistory();

  const [step, setStep] = useState("select-properties");
  const [selectedProperties, setSelectedProperties] = useState<IPropertySelected[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<ISelectedPlan | null>(null);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState("");
  const [sendResults, setSendResults] = useState<{
    total: number;
    completed: number;
    successful: number;
    failed: number;
  }>({
    total: 0,
    completed: 0,
    successful: 0,
    failed: 0,
  });

  const { data: servicePlanData } = useQuery(
    "service-plans",
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/admin?type=PLANS`
        )
        .then((res) => res.data)
  );

  const activePlans = servicePlanData?.filter((plan: ISelectedPlan) => !plan.archived) || [];

  const steps = [
    { 
      id: "select-properties", 
      label: tr("Select Properties"),
      warning: "",
      canAccess: () => true,
    },
    { 
      id: "select-plans", 
      label: tr("Select Plans"),
      warning: tr("Please select at least one property before proceeding"),
      canAccess: () => selectedProperties.length > 0
    },
    { 
      id: "confirm-send", 
      label: tr("Confirm"),
      warning: tr("Please select a plan before proceeding"),
      canAccess: () => selectedProperties.length > 0 && selectedPlan !== null
    },
    { 
      id: "success", 
      label: tr("Success"),
      warning: tr("You must complete all previous steps first"),
      canAccess: () => false // This step is only accessible after sending 
    },
  ];

  const boxStyle = "absolute border rounded-lg shadow-md overflow-x-hidden bg-white h-[calc(100vh_-_285px)] transition-all duration-500 ease-in-out";

  const backButton = () => {
    setStep("select-properties");
    setSelectedProperties([]);
    setSelectedPlan(null);
    setSendProgress("");
    setSendResults({
      total: 0,
      completed: 0,
      successful: 0,
      failed: 0,
    });
  };

  return (
    <div className="relative w-full h-full">
      {/* Overlay */}
      {/* {sending && (
        <>
          <div className="absolute top-0 right-0 bottom-0 left-0 bg-gray-300 z-30 opacity-70" />
          <div className="absolute top-1/3 right-1/2 z-40"><Spinner size={50} color="#ff4f00" /></div>
        </>
      )} */}

      <div className="mx-10 my-8 h-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">{tr("Bulk Send")}</h1>
          <Button
            onClick={() => {
              history.push("/service-plan-dashboard");
            }}
          >
            {tr("Return to Dashboard")}
          </Button>
        </div>

        {/* Steps */}
        <div className="flex justify-around text-center mb-4 border-b-[2px] pb-4">
          {steps.map((s, index) => {
            const isActive = s.id === step;
            const canNavigate = s.canAccess();
            
            return (
              <div
                key={s.id}
                onClick={() => {
                  if (canNavigate) {
                    setStep(s.id);
                  } else {
                    // Fixed: Call showSnackbar with the message string directly
                    showSnackbar(s.warning);
                  }
                }}
                className={`relative cursor-pointer px-4 py-2 rounded select-none ${
                  isActive
                    ? "bg-blue-100 text-blue-700 font-semibold"
                    : canNavigate 
                      ? "text-gray-500 hover:text-blue-600" 
                      : "text-gray-300 cursor-not-allowed"
                }`}
              >
                <div className="text-sm mb-1">{tr("Step")} {index + 1}</div>
                <div className="text-base">{s.label}</div>
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="relative pb-5 w-full h-[calc(100vh_-_270px)] overflow-x-hidden">
          <div className="flex bg-white transition-transform duration-500 ease-in-out w-full h-full">

            {/* Property Selection */}
            <div className={`
              ${boxStyle} px-4 pb-4 w-[calc(75%_-_1rem)] 
              ${step === "select-properties" ? "translate-x-0" : "-translate-x-full"}
            `}>
              <Properties
                stripped={true}
                selectedProperties={selectedProperties}
                setSelectedProperties={setSelectedProperties}
              />
            </div>

            {/* Main div used for selected properties + Confirm + Send */}
            <div className={`relative
              ${boxStyle} z-10 
              ${step === "select-properties" 
                ? "translate-x-[calc(300%_+_3rem)]" 
                : step === "success"
                ? "-translate-x-[120%]"
                : "-translate-x-0"}
              ${step === "confirm-send" ? "w-full" : "w-[calc(25%_-_1rem)]"}
            `}>
              <SelectPropertiesContainer
                {...{ 
                  selectedProperties, 
                  setSelectedProperties,
                  step,
                  setStep,
                  selectedPlan,
                  setSelectedPlan,
                  sending,
                  setSending,
                  sendProgress,
                  setSendProgress,
                  sendResults,
                  setSendResults,
                }}
              />
            </div>

            {/* Service Plan */}
            <div className={`
              ${boxStyle} p-4 w-3/4
              ${step === "select-properties" 
                ? " translate-x-[133.333%]"
                : step === "select-plans"
                ? "translate-x-1/3"
                : "-translate-x-full"
              }
            `}>
              <ServicePlanContainer
                data={activePlans}
                {...{  selectedProperties, setSelectedProperties }}
                {...{ selectedPlan, setSelectedPlan }}
                active={step === "select-plans"}
              />
            </div>

            {/* Success */}
            <div className={`
              ${boxStyle} p-4 w-full
              ${step === "success" ? "translate-x-0" : "translate-x-[100%]"}
            `}>
              <Success
                sending={sending}
                sendProgress={sendProgress}
                sendResults={sendResults}
                backButton={backButton}
                sendType={selectedPlan?.is_contract ? "contract" : "no-contract"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkSend;